/**
 * 密码哈希工具
 * crypto.subtle 仅在 HTTPS 环境下可用，HTTP 环境自动降级到 simpleHash
 */

/** 密码 salt，必须与后端 Edge Function 保持一致 */
const PASSWORD_SALT = 'salt_custom_app_2024';

/** 降级哈希：不依赖 crypto.subtle，HTTP 环境可用 */
const simpleHash = (str: string): string => {
  const salted = str + PASSWORD_SALT;
  let h1 = 5381;
  for (let i = 0; i < salted.length; i++) {
    h1 = ((h1 << 5) + h1) + salted.charCodeAt(i);
    h1 = h1 & h1;
  }
  let h2 = 0x811c9dc5;
  for (let i = 0; i < salted.length; i++) {
    h2 ^= salted.charCodeAt(i);
    h2 = (h2 * 0x01000193) >>> 0;
  }
  return (h1 >>> 0).toString(16).padStart(8, '0')
    + h2.toString(16).padStart(8, '0')
    + salted.length.toString(16).padStart(4, '0');
};

/** 无 salt 哈希（用于兼容旧数据） */
export const hashPasswordNoSalt = async (pwd: string): Promise<string> => {
  if (typeof crypto !== 'undefined' && crypto.subtle) {
    try {
      const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(pwd));
      return Array.from(new Uint8Array(buf))
        .map(b => b.toString(16).padStart(2, '0'))
        .join('');
    } catch {
      // fallback
    }
  }
  // 无 salt 的降级哈希
  let h1 = 5381;
  for (let i = 0; i < pwd.length; i++) {
    h1 = ((h1 << 5) + h1) + pwd.charCodeAt(i);
    h1 = h1 & h1;
  }
  let h2 = 0x811c9dc5;
  for (let i = 0; i < pwd.length; i++) {
    h2 ^= pwd.charCodeAt(i);
    h2 = (h2 * 0x01000193) >>> 0;
  }
  return (h1 >>> 0).toString(16).padStart(8, '0')
    + h2.toString(16).padStart(8, '0')
    + pwd.length.toString(16).padStart(4, '0');
};

/** 哈希密码（带 salt，与后端一致），优先使用 SHA-256 */
export const hashPassword = async (pwd: string): Promise<string> => {
  const salted = pwd + PASSWORD_SALT;
  if (typeof crypto !== 'undefined' && crypto.subtle) {
    try {
      const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(salted));
      return Array.from(new Uint8Array(buf))
        .map(b => b.toString(16).padStart(2, '0'))
        .join('');
    } catch {
      return simpleHash(pwd);
    }
  }
  return simpleHash(pwd);
};

/**
 * 验证密码（兼容旧数据无 salt 哈希）
 * @param pwd 用户输入的明文密码
 * @param storedHash 数据库中存储的 hash
 * @returns 是否匹配，以及是否是旧格式（需要迁移）
 */
export const verifyPassword = async (pwd: string, storedHash: string): Promise<{ valid: boolean; isLegacy: boolean }> => {
  // 先尝试新格式（带 salt）
  const newHash = await hashPassword(pwd);
  if (newHash === storedHash) {
    return { valid: true, isLegacy: false };
  }
  // 再尝试旧格式（无 salt）
  const oldHash = await hashPasswordNoSalt(pwd);
  if (oldHash === storedHash) {
    return { valid: true, isLegacy: true };
  }
  return { valid: false, isLegacy: false };
};